/**
 * Purpose: Manages the state for media message attachments (images and videos) in the chat
 *
 * How it works:
 * 1. When a media file is attached:
 *    - Validates file type and size
 *    - Creates thumbnails for preview
 *    - Manages URLs and cleanup
 * 2. During upload:
 *    - Tracks upload status
 *    - Handles file references
 *    - Manages preview URLs
 *
 * Key features:
 * - Separate handling for images and videos
 * - Automatic thumbnail generation
 * - Memory leak prevention via URL cleanup
 * - Size limit enforcement with translations
 * - Proper cleanup on discard/reset
 */

import { create } from "zustand";
import { generateUuid7 } from "@/utils/uuid7";

import {
  createImageThumbnail,
  createPlaceholderVideoThumbnail,
  createVideoThumbnail,
} from "@/utils/createThumbnail";
import { ALLOWED_IMAGE_TYPES, ALLOWED_VIDEO_TYPES } from "@/config/constants";
import { isUserAuthenticated } from "@/middleware/auth";
import { isArchitectComposerActive } from "@/utils/architectComposer";
import { attachmentSizeRejection } from "@/utils/attachmentSizeLimit";
import { useSubscriptionState } from "./SubscriptionState";
import { toast } from "@/hooks/useToast";
import i18next from "i18next";

/**
 * State interface for media message attachments
 *
 * @property isUploading - Tracks if a media file is currently being uploaded
 * @property newMediaAttachment - The actual File object being processed
 * @property mediaAttachmentFileType - Distinguishes between image and video attachments
 * @property newMediaThumbnailId - Unique ID for the generated thumbnail
 * @property newMediaAttachmentId - Unique ID for the media attachment
 * @property thumbnailBlob - Generated thumbnail for preview
 * @property newMediaAttachmentUrl - ObjectURL for media preview
 */
type State = {
  isUploading: boolean;
  newMediaAttachment: File | null;
  mediaAttachmentFileType: "image" | "video";
  newMediaThumbnailId: string;
  newMediaAttachmentId: string;
  thumbnailBlob: Blob | null;
  newMediaAttachmentUrl: string;
};

/**
 * Actions for managing media message attachments
 *
 * @property setIsUploading - Updates upload status
 * @property setNewMediaAttachment - Sets the current media file
 * @property setMediaAttachmentFileType - Updates the media type (image/video)
 * @property setNewMediaThumbnailId - Sets thumbnail identifier
 * @property setNewMediaAttachmentId - Sets media attachment identifier
 * @property setThumbnailBlob - Updates thumbnail data
 * @property setNewMediaAttachmentUrl - Updates preview URL
 * @property discardMediaAttachment - Cleans up resources and resets state
 * @property handleMediaAttachment - Main handler for new media attachments
 * @property resetState - Resets to initial state
 */
type Actions = {
  setIsUploading: (isUploading: boolean) => void;
  setNewMediaAttachment: (file: File | null) => void;
  setMediaAttachmentFileType: (type: "image" | "video") => void;
  setNewMediaThumbnailId: (id: string) => void;
  setNewMediaAttachmentId: (id: string) => void;
  setThumbnailBlob: (blob: Blob | null) => void;
  setNewMediaAttachmentUrl: (url: string) => void;
  discardMediaAttachment: () => void;
  handleMediaAttachment: (file: File) => Promise<void>;
  /** Send-path accessor BY attachment id (from the send handler's closure). */
  waitForStagedThumbnail: (attachmentId: string) => Promise<Blob | null>;
  resetState: () => void;
};

const initialState: State = {
  isUploading: false,
  newMediaAttachment: null,
  mediaAttachmentFileType: "image",
  newMediaThumbnailId: "",
  newMediaAttachmentId: "",
  thumbnailBlob: null,
  newMediaAttachmentUrl: "",
};

// Background thumbnail captures keyed by attachment id. Module-scoped (a
// promise handle is not renderable data). KEY PROPERTY: the send flow clears
// the staging state BEFORE the send functions run (optimistic input clear),
// so the send path resolves its thumbnail through THIS map using the
// attachment id it captured in its closure — never through current state.
// Settled promises retain their value, so post-clear resolution is free.
// Capped so abandoned selections can't accumulate retained Files.
const pendingThumbnailCaptures = new Map<string, Promise<Blob | null>>();
const MAX_PENDING_CAPTURES = 3;
function rememberCapture(id: string, promise: Promise<Blob | null>) {
  pendingThumbnailCaptures.set(id, promise);
  while (pendingThumbnailCaptures.size > MAX_PENDING_CAPTURES) {
    const oldest = pendingThumbnailCaptures.keys().next().value as string;
    pendingThumbnailCaptures.delete(oldest);
    stagedVideoDims.delete(oldest);
  }
}

// Native video dimensions read off the decoded element during frame capture,
// keyed by attachment id like the capture map above (and evicted with it).
// Set ONLY when a real frame was captured — the placeholder fallback is a
// hardcoded 16:9 tile, and recording its dims would embed confidently-wrong
// data in the message meta (worse than the legacy no-dims state). The send
// path resolves these BY ID after awaiting the capture, so the map is always
// settled when read.
const stagedVideoDims = new Map<string, { width: number; height: number }>();

/** Send-path lookup: the staged video's native dims, if a real frame capture
 *  produced them. `undefined` for images, placeholder fallbacks, unknown ids. */
export function getStagedVideoDims(
  attachmentId: string,
): { width: number; height: number } | undefined {
  return stagedVideoDims.get(attachmentId);
}

export const useMediaMessageSenderState = create<State & Actions>()(
  (set, get) => ({
    // State
    ...initialState,

    // Actions
    setIsUploading: (isUploading: boolean) => set({ isUploading }),
    setNewMediaAttachment: (file: File | null) =>
      set({ newMediaAttachment: file }),
    setMediaAttachmentFileType: (type: "image" | "video") =>
      set({ mediaAttachmentFileType: type }),
    setNewMediaThumbnailId: (id: string) => set({ newMediaThumbnailId: id }),
    setNewMediaAttachmentId: (id: string) => set({ newMediaAttachmentId: id }),
    setThumbnailBlob: (blob: Blob | null) => set({ thumbnailBlob: blob }),
    setNewMediaAttachmentUrl: (url: string) =>
      set({ newMediaAttachmentUrl: url }),

    /**
     * Cleans up resources and resets state when discarding media attachment
     * - Revokes object URLs to prevent memory leaks
     * - Resets all state to initial values
     */
    discardMediaAttachment: () => {
      const { newMediaAttachmentUrl } = get();
      if (newMediaAttachmentUrl) {
        URL.revokeObjectURL(newMediaAttachmentUrl);
      }
      set(initialState);
    },

    /**
     * Handles new media file attachments
     *
     * Process:
     * 1. Validates user authentication
     * 2. Determines media type (image/video)
     * 3. Validates file size against limits
     * 4. Generates thumbnails for preview
     * 5. Creates object URLs for preview
     * 6. Updates state with new media data
     *
     * @param file - The media file to process
     */
    handleMediaAttachment: async (file: File) => {
      const userFingerPrint = isUserAuthenticated();
      if (!userFingerPrint) {
        return;
      }

      const isImage = ALLOWED_IMAGE_TYPES.includes(file.type);
      const isVideo = ALLOWED_VIDEO_TYPES.includes(file.type);

      // IDs are generated inside each SUCCESS `set(...)` below (never up-front,
      // see #986). If validation or thumbnail generation fails, every early
      // return then leaves the previously-staged (valid) attachment untouched
      // and internally consistent — file, thumbnail, and both IDs always move
      // together. Pre-setting IDs here would strand fresh IDs on the prior
      // file when a later re-selection fails.
      if (isImage) {
        // Resolves the per-plan image cap for the destination scope — the
        // Architect image ladder in an Architect composer, the regular per-plan
        // ladder elsewhere. Shares its decision with the send-time check in
        // messages.ts so the two cannot disagree. Deliberately BEFORE the
        // thumbnail generation below — decoding an oversized image just to
        // discard it is work nobody asked for.
        const sizeRejection = attachmentSizeRejection(file.size, {
          fileType: file.type,
          isArchitect: isArchitectComposerActive(),
          plan: useSubscriptionState.getState().subscription.plan,
        });
        if (sizeRejection) {
          toast({
            duration: 1500,
            description: sizeRejection,
            variant: "destructive",
          });
          return;
        }

        // createImageThumbnail -> createImageBitmap rejects (InvalidStateError:
        // "The source image could not be decoded.") on a corrupt/unsupported
        // file that still passed the MIME allowlist. This action is invoked
        // fire-and-forget, so an uncaught rejection becomes an unhandled
        // rejection and the attachment silently fails — catch it and surface a
        // toast instead (#977).
        try {
          // 560 = max bubble width (280 CSS px) × 2 for retina. The generator
          // fits the LONG side to this box ("contain"), so anything smaller
          // under-resolves aspect-correct bubbles.
          const _thumbnailBlob = await createImageThumbnail({
            file,
            maxWidth: 560,
            maxHeight: 560,
          });
          if (!_thumbnailBlob) {
            return;
          }

          const _newMediaAttachmentUrl = URL.createObjectURL(file);
          const imageAttachmentId = generateUuid7();
          rememberCapture(imageAttachmentId, Promise.resolve(_thumbnailBlob));

          set({
            newMediaAttachment: file,
            thumbnailBlob: _thumbnailBlob,
            newMediaAttachmentUrl: _newMediaAttachmentUrl,
            mediaAttachmentFileType: "image",
            // IDs generated here on success (never up-front) so a failed
            // re-selection can't strand fresh IDs on the prior file (#986).
            // The attachment id doubles as the capture-map key (send path).
            newMediaThumbnailId: generateUuid7(),
            newMediaAttachmentId: imageAttachmentId,
          });
        } catch {
          toast({
            duration: 1500,
            description: i18next.t("errorCreatingImageThumbnail"),
            variant: "destructive",
          });
        }
      }

      if (isVideo) {
        // Same shared decision as the image and document branches. Resolution
        // depends on the destination scope: in a regular chat video uses the
        // per-plan REGULAR_VIDEO_LIMITS ladder clamped to MAX_VIDEO_FILE_SIZE;
        // in an Architect chat it is BLOCKED (no matrix cell → not offered), so
        // this rejects it here with videoNotSupportedInArchitectChat rather than
        // letting it stage. It also refuses 0-byte files — otherwise an empty
        // video stages fully and is only refused at send time, after the
        // composer has been cleared, inconsistent with image and PDF handling.
        const sizeRejection = attachmentSizeRejection(file.size, {
          fileType: file.type,
          isArchitect: isArchitectComposerActive(),
          plan: useSubscriptionState.getState().subscription.plan,
        });
        if (sizeRejection) {
          toast({
            duration: 1500,
            description: sizeRejection,
            variant: "destructive",
          });
          return;
        }

        // STAGE FIRST: the video appears in the input instantly — file, URL
        // and both IDs set together (#986 invariant), thumbnail pending.
        // Frame capture runs in the BACKGROUND: it needs a real media decode
        // (hundreds of ms; historically the await here was the "selection
        // takes seconds" latency). The result is patched in only if this
        // selection is still the staged one (ID guard), so rapid re-selection
        // or discard drops stale captures. Capture failure degrades to a
        // placeholder tile — a preview nicety must never block the send
        // (#977; the iPhone send-blocker).
        const attachmentId = generateUuid7();
        const thumbnailId = generateUuid7();
        const _newMediaAttachmentUrl = URL.createObjectURL(file);

        set({
          newMediaAttachment: file,
          thumbnailBlob: null,
          newMediaAttachmentUrl: _newMediaAttachmentUrl,
          mediaAttachmentFileType: "video",
          newMediaThumbnailId: thumbnailId,
          newMediaAttachmentId: attachmentId,
        });

        const capture = (async (): Promise<Blob | null> => {
          let thumb: Blob | null = null;
          try {
            // Both bounds explicit: (file, 560) would leave maxHeight at its
            // 200 default — an asymmetric box that crushed portrait video
            // thumbnails to 112×200. Same 560 retina target as images.
            const captured = await createVideoThumbnail(file, 560, 560);
            thumb = captured?.blob ?? null;
            if (captured?.width && captured?.height) {
              stagedVideoDims.set(attachmentId, {
                width: captured.width,
                height: captured.height,
              });
            }
          } catch {
            // fall through to the placeholder
          }
          if (!thumb) {
            thumb = await createPlaceholderVideoThumbnail().catch(() => null);
            if (thumb && get().newMediaAttachmentId === attachmentId) {
              toast({
                duration: 2500,
                description: i18next.t("videoPreviewUnavailable"),
              });
            }
          }
          if (thumb && get().newMediaAttachmentId === attachmentId) {
            set({ thumbnailBlob: thumb });
          }
          return thumb;
        })();
        rememberCapture(attachmentId, capture);
      }
    },

    /**
     * Resolves an attachment's thumbnail for the send path BY ID — the id the
     * send handler captured in its closure. Deliberately independent of
     * current state: handleSendMessage clears the staging state before the
     * send functions run (optimistic input clear), so reading state here
     * would always see the post-clear void (that exact bug shipped once).
     * Bounded: captures self-time-out and fall back to a placeholder. Null
     * only when even the placeholder failed (broken canvas) or the id is
     * unknown.
     */
    waitForStagedThumbnail: async (attachmentId: string) => {
      if (!attachmentId) {
        return null;
      }
      const { thumbnailBlob, newMediaAttachmentId } = get();
      if (thumbnailBlob && newMediaAttachmentId === attachmentId) {
        return thumbnailBlob;
      }
      return (await pendingThumbnailCaptures.get(attachmentId)) ?? null;
    },

    resetState: () => set(initialState),
  }),
);
